import { useMemo, useState } from 'react';
import { Activity, ChevronDown, LineChart as LineChartIcon, Radar as RadarIcon } from 'lucide-react';
import type { ForumHealthMetric, ForumHealthRadarPoint, SnapshotHistoryEntry } from '../types';
import {
  DualMetricRadar,
  DualMetricCategoryTrendChart,
  formatYearMonth,
  type CategoryTrendShape,
} from './forumHealth';
import { SCHEMA_STATE_DIRECTION } from '../lib/snapshotFlags';

// How many recent months the line view plots.
const TREND_WINDOW = 12;

// Personal State/Direction health visuals — the same radar/line pair the
// leadership dashboard shows for a whole forum, scoped to the member's own
// snapshots. Built entirely from the already-loaded snapshot history, so no
// extra endpoint (or permission) is involved. Renders nothing until the
// member has at least one finalized Snapshot 2.0.
export default function MySnapshotHealth({ history }: { history: SnapshotHistoryEntry[] }) {
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState<'radar' | 'line'>('radar');
  const [hiddenCategories, setHiddenCategories] = useState<Set<string>>(new Set());

  const v2Entries = useMemo(
    () =>
      history
        .filter((h) => h.schema_version === SCHEMA_STATE_DIRECTION)
        .sort((a, b) => a.month.localeCompare(b.month)),
    [history],
  );

  if (v2Entries.length === 0) return null;

  const latest = v2Entries[v2Entries.length - 1];
  const previous = v2Entries.length > 1 ? v2Entries[v2Entries.length - 2] : null;

  const ratingValue = (
    entry: SnapshotHistoryEntry | null,
    category: string,
    metric: ForumHealthMetric,
  ): number | null => {
    const rating = entry?.ratings.find((r) => r.category === category);
    if (!rating || rating.skipped) return null;
    return rating[metric];
  };

  const radarPoints = (metric: ForumHealthMetric): ForumHealthRadarPoint[] => {
    const categories = [...new Set([
      ...latest.ratings.map((r) => r.category),
      ...(previous?.ratings.map((r) => r.category) ?? []),
    ])];
    return categories.map((category) => ({
      category,
      current_avg: ratingValue(latest, category, metric),
      previous_avg: ratingValue(previous, category, metric),
    }));
  };

  const trendWindow = v2Entries.slice(-TREND_WINDOW);
  const trendShape = (metric: ForumHealthMetric): CategoryTrendShape => {
    const categories = [...new Set(trendWindow.flatMap((e) => e.ratings.map((r) => r.category)))];
    return {
      month_labels: trendWindow.map((e) => formatYearMonth(e.month.slice(0, 7))),
      categories: categories.map((category) => ({
        category,
        values: trendWindow.map((e) => ratingValue(e, category, metric)),
      })),
    };
  };

  const statePoints = radarPoints('state');
  const directionPoints = radarPoints('direction');
  const chipCategories = view === 'radar'
    ? statePoints.map((p) => p.category)
    : [...new Set(trendWindow.flatMap((e) => e.ratings.map((r) => r.category)))];

  const toggleCategory = (category: string) => {
    setHiddenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const currentLabel = latest.month_display;
  const previousLabel = previous?.month_display ?? 'Previous month';
  const allHidden = chipCategories.length > 0 && chipCategories.every((c) => hiddenCategories.has(c));

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-gray-50 transition"
      >
        <div className="min-w-0">
          <h2 className="font-semibold text-navy flex items-center gap-2">
            <Activity size={20} className="text-gold shrink-0" />
            My Health
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {currentLabel}{previous ? ` vs ${previousLabel}` : ''}
          </p>
        </div>
        <ChevronDown
          size={20}
          className={`text-gray-400 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      {expanded && (
        <div className="px-5 pb-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <p className="text-sm text-gray-500">
          {view === 'radar'
            ? `Your State & Direction by area, ${currentLabel}${previous ? ` vs ${previousLabel}` : ''}.`
            : `Your State & Direction by area over your last ${trendWindow.length} snapshots.`}
        </p>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden shrink-0" role="group" aria-label="Chart view">
          <button
            type="button"
            onClick={() => setView('radar')}
            aria-pressed={view === 'radar'}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition ${
              view === 'radar' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <RadarIcon size={14} /> Radar
          </button>
          <button
            type="button"
            onClick={() => setView('line')}
            aria-pressed={view === 'line'}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition ${
              view === 'line' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <LineChartIcon size={14} /> Line
          </button>
        </div>
      </div>

      {view === 'radar' ? (
        allHidden ? (
          <div className="h-80 flex items-center justify-center">
            <p className="text-sm text-gray-400 italic">
              All areas hidden — turn an area back on below to see the radar.
            </p>
          </div>
        ) : (
          <DualMetricRadar
            statePoints={statePoints.filter((p) => !hiddenCategories.has(p.category))}
            directionPoints={directionPoints.filter((p) => !hiddenCategories.has(p.category))}
            currentLabel={currentLabel}
            previousLabel={previousLabel}
            scaleNotes={{
              state: 'Scale: 1 Breaking · 2 Strained · 3 Healthy · 4 Thriving (skipped areas excluded).',
              direction: 'Scale: 1 Slipping · 2 Maintaining · 3 Growing (skipped areas excluded).',
            }}
          />
        )
      ) : (
        <DualMetricCategoryTrendChart
          stateTrend={trendShape('state')}
          directionTrend={trendShape('direction')}
          hiddenCategories={hiddenCategories}
          emptyLabel="Your trend will appear here as you complete more snapshots."
        />
      )}

      {chipCategories.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {chipCategories.map((category) => {
            const hidden = hiddenCategories.has(category);
            return (
              <button
                key={category}
                type="button"
                onClick={() => toggleCategory(category)}
                aria-pressed={!hidden}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                  hidden
                    ? 'bg-gray-50 text-gray-400 border-gray-200 line-through'
                    : 'bg-navy/5 text-navy border-navy/20 hover:bg-navy/10'
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>
      )}
        </div>
      )}
    </div>
  );
}
